import { API_BASE_URL, isSimple } from 'config'
import React from 'react'

import { kea } from 'kea'
import { put, call, take } from 'redux-saga/effects'
import { delay } from 'redux-saga'

import PropTypes from 'prop-types'
import axios from 'axios'
import DataLogicV2 from 'containers/AppV2/logic'

import { Validations } from 'validations'

import omit from 'lodash/omit'
import find from 'lodash/find'
import forEach from 'lodash/forEach'

import EgIcon from './images/electricityplusgas.component.svg'
import EIcon from './images/electricity.component.svg'
import GIcon from './images/gas.component.svg'

const DISTRICTS_ENDPOINT = `${API_BASE_URL}/public/district/`
const CITIES_ENDPOINT = `${API_BASE_URL}/public/municipality/`
const CONSUMER_PROFILE = `${API_BASE_URL}/public/simulator/consumer_profile/`

export const LOADING_COUNT = 2

const fields = {
  offerType: [Validations.isUndefined],
  district: [Validations.isUndefined],
  municipality: [Validations.isUndefined],
  habitants: [Validations.isUndefined],
  hasSolarSystem: [Validations.isUndefined],
  hasElectricCar: [Validations.isUndefined],
  hasNaturalGasHeating: [Validations.isUndefined],
}

/* eslint-disable camelcase */
export default kea({
  path: () => ['scenes', 'profileV2'],

  actions: () => ({
    setDistricts: (districts) => ({ districts }),
    setData: (data) => ({ data }),
    setCities: (cities) => ({ cities }),
    setInvalid: (invalid) => ({ invalid }),
    setCurrentLocation: (currentLocation) => ({ currentLocation }),
    increment: () => ({}),

    removeCities: (cities) => ({ cities }),

    fetchDistricts: (limit) => ({ limit }),
    fetchCities: (district) => ({ district }),
    fetchData: () => ({}),

    setDataFetchError: (fetchDataError) => ({ fetchDataError }),
    setDistrictsFetchError: (fetchDistrictError) => ({ fetchDistrictError }),
    setCitiesFetchError: (fetchCitiesError) => ({ fetchCitiesError }),
  }),

  reducers: ({ actions }) => ({
    currentLocalization: [
      null,
      PropTypes.any,
      {
        [actions.setCurrentLocation]: (state, payload) =>
          payload.currentLocation,
      },
    ],

    districts: [
      [],
      PropTypes.array,
      {
        [actions.setDistricts]: (state, payload) => [...payload.districts],
      },
    ],

    cities: [
      [],
      PropTypes.array,
      {
        [actions.setCities]: (state, payload) => [...payload.cities],
        [actions.removeCities]: (state, payload) => omit(state, payload.cities),
      },
    ],

    data: [
      {},
      PropTypes.object,
      {
        [actions.setData]: (state, payload) => ({ ...payload.data }),
      },
    ],

    fields: [fields, PropTypes.object, {}],

    invalid: [
      {},
      PropTypes.array,
      {
        [actions.setInvalid]: (_, payload) => payload.invalid,
      },
    ],

    loading: [
      LOADING_COUNT,
      PropTypes.bool,
      {
        [actions.setDistricts]: (state) => state && state - 1,
        [actions.setData]: (state) => state && state - 1,
        [actions.setDataFetchError]: () => 0,
        [actions.setCities]: (state) => state && state - 1,
        [actions.increment]: (state) => state + 1,
      },
    ],
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchDistricts]: workers.fetchDistricts,
    [actions.setDistricts]: workers.setFromLocation,
    [actions.setCities]: workers.setMunicipalityFromLocation,
    [actions.setCurrentLocation]: workers.setFromLocation,
    [actions.setDistrict]: workers.fetchCities,
    [actions.fetchCities]: workers.fetchCities,
    [actions.fetchData]: workers.fetchData,
  }),

  start: function*() {
    const { fetchDistricts, fetchData, fetchCities, increment } = this.actions
    const { simulationFinished } = yield DataLogicV2.get('finished')
    const values = yield DataLogicV2.get('values')
    const settings = yield DataLogicV2.get('settings')

    // Only fetch the data once
    // No need to do it when whe hit Prev for example
    yield delay(800)
    if (simulationFinished || !values.simulationCode) {
      yield put(fetchDistricts(100))
      yield take()

      if (settings.isFromProfile || settings.isFromUrl) {
        yield put(increment())
        yield put(fetchCities())
      }

      yield put(fetchData())
    }
  },

  workers: {
    /**
     * Fetches the needed data
     * @param {*} action
     */
    *fetchData(action) {
      const { setData, setDataFetchError, setValues } = this.actions
      const { simulationFinished } = yield DataLogicV2.get('finished')
      const settings = yield DataLogicV2.get('settings')
      const values = yield DataLogicV2.get('values')
      const params = { format: 'json', type: isSimple ? 's' : 'b' }
      try {
        const data = yield call(axios.post, CONSUMER_PROFILE, params)

        const offerTypes =
          data.data &&
          data.data.offer_types
            .sort((a, b) => a.order - b.order)
            .map(({ id, slug, name }) => {
              let icon
              switch (slug) {
                case 'e':
                  icon = <EIcon />
                  break
                case 'g':
                  icon = <GIcon />
                  break
                case 'eg':
                  icon = <EgIcon />
                  break
                default:
                  icon = <EIcon />
              }
              return {
                text: (name + '').replace(' + ', '<br />+<br />'),
                value: id,
                icon,
                id,
                slug,
              }
            })

        // Set the default values after getting the data
        const simulationCode = data.data.simulation_code
        let defaultValues = {}

        if (
          !simulationFinished &&
          !settings.isFromProfile &&
          !settings.isFromUrl
        ) {
          defaultValues = {
            simulationCode,
            habitants: undefined,
            district: undefined,
            municipality: undefined,
            offerType: find(offerTypes, ({ slug }) => slug === 'e').id,
            hasElectricCar: false,
            hasNaturalGasHeating: false,
            hasSolarSystem: false,
          }
        } else {
          // Reset simulation code
          defaultValues = {
            simulationCode,
          }
        }

        if (values.district) {
          defaultValues = {
            ...defaultValues,
            district: values.district,
          }
        }

        yield put(setValues(defaultValues))
        yield put(
          setData({
            offerTypes,
          })
        )

        const { setFinished } = this.actions
        const goToResults = yield DataLogicV2.get('goToResults')
        const dataSimulationChanged = yield DataLogicV2.get(
          'dataSimulationChanged'
        )

        if (goToResults && !dataSimulationChanged) {
          setFinished({ profileFinished: true })
          this.props.router.push('/resultados')
        }
      } catch (error) {
        yield put(setDataFetchError(error))
      }
    },

    /**
     * Fetchs all Districts
     * @param {*} action
     */
    *fetchDistricts(action) {
      const { limit } = action.payload
      const { setDistricts, setDistrictsFetchError } = this.actions
      const params = { limit, format: 'json' }

      try {
        const districts = yield call(axios.get, DISTRICTS_ENDPOINT, { params })
        const filteredDistricts = districts.data.results.map(
          ({ id, name }) => ({
            label: name,
            value: id,
          })
        )

        yield put(setDistricts(filteredDistricts))
      } catch (error) {
        yield put(setDistrictsFetchError(error))
      }
    },

    /**
     * Fetches all municipalities from a district
     * @param {*} action
     */
    *fetchCities(action) {
      const { district } = action.payload
      const { setCities, setCitiesFetchError } = this.actions
      const params = { limit: 1000, format: 'json', district }

      try {
        const cities = yield call(axios.get, CITIES_ENDPOINT, { params })
        let { results } = cities.data

        results = results.map(({ id, name, district }) => ({
          value: id,
          label: name,
          district: district.id,
        }))

        yield put(setCities(results))
      } catch (error) {
        yield put(setCitiesFetchError(error))
      }
    },

    *setFromLocation() {
      const { setValues } = this.actions

      const districts = yield this.get('districts')
      const currentLocation = yield this.get('currentLocalization')

      if (currentLocation && !!currentLocation.length) {
        let distrito = null
        forEach(currentLocation, ({ long_name }) => {
          let find =
            districts &&
            districts.find(
              ({ label }) => label.toLowerCase() === long_name.toLowerCase()
            )
          if (find) {
            distrito = find
            return false
          }
        })

        if (distrito) {
          yield put(setValues({ district: distrito.value }))
        }
      }
    },

    *setMunicipalityFromLocation() {
      const { setValues } = this.actions

      const cities = yield this.get('cities')
      const currentLocation = yield this.get('currentLocalization')
      let cidade = null

      if (currentLocation && !!currentLocation.length) {
        // Search First for administrative area level 2 and 3
        forEach(currentLocation, (location) => {
          let find =
            cities &&
            cities.find(
              ({ label }) =>
                label.toLowerCase() === location.long_name.toLowerCase() &&
                (location.types[0] === 'administrative_area_level_2' ||
                  location.types[0] === 'administrative_area_level_3')
            )
          if (find) {
            cidade = find
            return false
          }
        })

        // If search is empty search by the name
        if (!cidade) {
          forEach(currentLocation, (location) => {
            let find =
              cities &&
              cities.find(
                ({ label }) =>
                  label.toLowerCase() === location.long_name.toLowerCase()
              )
            if (find) {
              cidade = find
              return false
            }
          })
        }

        if (cidade) {
          yield put(setValues({ municipality: cidade.value }))
        }
      }
    },
  },

  connect: {
    actions: [DataLogicV2, ['setValue', 'setValues', 'setFinished']],
    props: [
      DataLogicV2,
      ['values', 'finished', 'translations', 'iframed', 'goToResults'],
    ],
  },
})



// WEBPACK FOOTER //
// ./src/containers/ProfileV2/logic.js